/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  colors,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { debounce } from 'lodash';
import { useNavigate, useParams } from 'react-router';

import { DecisionDialog, LoaderBar, Page } from 'src/components';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import ProductInItemCard from './components/ProductInItemCard';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import { PurchaseOrderDetails } from 'src/redux/slices/purchase-order/types';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { useSnackBar } from 'src/hooks';
import InfoIcon from '@material-ui/icons/Info';
import useResolution from 'src/hooks/useResolution';
import usePrompt from 'src/utils/navigation-prompt';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

// // TODO: Pakilipat sa /types ng slice ng product-in
// export type POProductInDetails = {
//   po_id?: number;
//   po_no?: string;
//   products?: POProductInItem[];
// };

// // TODO: Pakilipat sa /types ng slice ng product-in
// export type POProductInItem = {
//   purchase_order_product_id?: number;
//   quantity?: number;
//   product_id?: number;
//   product_name?: string;
//   sku?: string;
//   category?: Category;
//   dealer_price?: number;
//   amount?: string;
// };

const { actions: purchaseOrderActions } = slices.purchaseOrder;

const ProductInDetails = () => {
  const { id } = useParams();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const { getBranches } = useBranchInfo();
  const { sm } = useResolution();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [poProductInData, setPOProductInData] = useState<PurchaseOrderDetails>(
    {}
  );
  const [origDRNo, setOrigDRNo] = useState<string>();
  const [openDialog, setOpenDialog] = useState(false);
  const [openForcheckingDialog, setOpenForCheckingDialog] = useState(false);

  const onFetchPODetails = async () => {
    // navigate(`/app/branches/${branch.id}`, { state: branch });
    //  TODO: Move to thunk
    setIsLoading(true);
    // const response = await Api.get(`/po/product_in/get/${id}`);

    const response = unwrapResult(
      await dispatch(
        purchaseOrderActions.getPurchaseOrderDetailsThunk(Number(id))
      )
    );

    // TODO: Move to thunk and slice
    if (response?.success && response?.originalData) {
      setPOProductInData(response?.originalData?.data);
      if (response?.originalData?.data?.dr_no) {
        setOrigDRNo(response?.originalData?.data?.dr_no);
      }
    }

    setIsLoading(false);
  };

  const onUpdateDRNo = async () => {
    // Create thunk to update dr no lang.
    // or kahit api na lng directly

    if (!poProductInData?.dr_no) {
      snackbar.show({
        message: 'Enter a valid DR No.',
        severity: 'error',
        useSound: true
      });
      return;
    }

    if (Number(poProductInData?.si_percentage) > 0 && !poProductInData?.si_no) {
      snackbar.show({
        message: 'This P.O. has SI Percentage, SI No input is required',
        severity: 'error',
        useSound: true
      });
      return;
    }

    setIsLoading(true);
    const onUpdateDRNoResponse = unwrapResult(
      await dispatch(
        purchaseOrderActions.updatePurchaseOrderDRNoThunk({
          po_id: poProductInData?.po_id,
          dr_no: poProductInData?.dr_no,
          si_no: poProductInData?.si_no
        })
      ).finally(() => {
        setIsLoading(false);
      })
    );

    if (onUpdateDRNoResponse?.success) {
      alert('DR No. updated successfully!');
      onFetchPODetails();
    } else {
      alert('Failed to update DR No.');
    }
  };

  const onClickUpdateStatus = async (newStatus: string) => {
    if (newStatus === poProductInData?.status) {
      snackbar.show({
        message: `Status is already "${newStatus}"`,
        severity: 'error',
        useSound: true
      });
      return;
    }

    setIsLoading(true);

    const clonedNewValue = { status: newStatus, po_id: poProductInData?.po_id };
    const clonedOldValues = {
      status: poProductInData?.status,
      po_id: poProductInData?.po_id
    };

    try {
      const response = unwrapResult(
        await dispatch(
          purchaseOrderActions.updatePurchaseOrderThunk({
            newValue: clonedNewValue,
            oldValue: clonedOldValues
          })
        )
      );

      if (response?.success && response?.originalData?.data) {
        setOpenForCheckingDialog(false);
        snackbar.show({
          message: response?.message || 'Purchase Order status updated',
          severity: 'success'
        });

        onFetchPODetails();
      } else {
        snackbar.show({
          message: `${response?.message || 'Failed to update status'}, ${
            response?.errors ? JSON.stringify(response?.errors) : ''
          }`,
          severity: 'error'
        });
      }
    } catch (error) {
      snackbar.show({
        message: 'Something went wrong during update.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeDRNo = (newDR: string) => {
    setPOProductInData((prevState) => ({
      ...prevState,
      dr_no: newDR
    }));
  };

  const onChangeSINo = (newSI: string) => {
    setPOProductInData((prevState) => ({
      ...prevState,
      si_no: newSI
    }));
  };

  usePrompt(
    `There are some unsaved changes. Are you sure you want to leave?`,
    ['for_delivery', 'done_product_in'].includes(poProductInData.status ?? '')
  );

  useEffect(() => {
    onFetchPODetails();
    getBranches('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!origDRNo && poProductInData.status !== 'for_delivery' && !isLoading) {
      setOpenDialog(true);
    }
  }, [origDRNo, poProductInData.status, isLoading]);

  // TODO: Fix shitty UI
  return (
    <Page className={classes.root} title="Product In">
      <Container maxWidth={false}>
        <Box mt={2}>
          <LoaderBar isLoading={isLoading} />
        </Box>
        <Box>
          <Typography color="textPrimary" gutterBottom variant="h3">
            PO: {poProductInData?.order_no}
          </Typography>
        </Box>

        {origDRNo || isLoading ? (
          <>
            <Typography
              gutterBottom
              variant="h4"
              style={{
                color: colors.blueGrey[700]
              }}
            >
              DR: {origDRNo}
            </Typography>
            {poProductInData.status === 'done_product_in' && (
              <Box
                display="flex"
                alignItems={sm ? 'flex-start' : 'center'}
                flexDirection={sm ? 'column' : 'row'}
                mt={1}
                style={{ gap: 5 }}
              >
                <Box display="flex" alignItems="center" style={{ gap: 5 }}>
                  <InfoIcon
                    fontSize="small"
                    style={{ color: colors.green[500] }}
                  />
                  <Typography
                    variant="body2"
                    style={{ color: colors.green[500] }}
                  >
                    Product Intake Complete.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => setOpenForCheckingDialog(true)}
                  style={sm ? { marginTop: 8 } : {}}
                >
                  For PO Checking
                </Button>
              </Box>
            )}

            <div style={{ marginTop: 10 }}>
              {poProductInData?.internal_remarks && (
                <Alert severity="info">
                  <Typography variant="h6" style={{ whiteSpace: 'pre-line' }}>
                    {poProductInData?.internal_remarks}
                  </Typography>
                </Alert>
              )}
            </div>
          </>
        ) : (
          <Grid
            style={{ marginTop: 10 }}
            alignItems="center"
            container
            spacing={2}
          >
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                onChange={(e) => onChangeDRNo(e.target.value)}
                value={poProductInData?.dr_no}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AspectRatioIcon />
                    </InputAdornment>
                  )
                }}
                placeholder="INPUT DR NO. REQUIRED"
                helperText="This can only be saved ONCE. Please double check."
                variant="outlined"
              />
            </Grid>
            {Number(poProductInData?.si_percentage) > 0 ? (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  onChange={(e) => onChangeSINo(e.target.value)}
                  value={poProductInData?.si_no}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AspectRatioIcon />
                      </InputAdornment>
                    )
                  }}
                  placeholder="INPUT SI NO. REQUIRED"
                  helperText="This can only be saved ONCE. Please double check."
                  variant="outlined"
                />
              </Grid>
            ) : null}

            <Grid item xs={12} sm={6}></Grid>
            <Grid
              item
              xs={12}
              sm={6}
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={onUpdateDRNo}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        )}

        {origDRNo
          ? poProductInData?.products?.map((product, index) => (
              <ProductInItemCard
                key={index}
                poDetails={poProductInData}
                purchaseOrderProductData={product}
                fetchPODetails={onFetchPODetails}
              />
            ))
          : null}
      </Container>

      <Dialog open={openDialog} aria-labelledby="access-denied-dialog-title">
        <DialogTitle id="access-denied-dialog-title">Access Denied</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            You cannot proceed with this action because the DR No. is not set
            and the status is Not For Delivery.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              window.location.href = '/app/purchase-order';
            }}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <DecisionDialog
        isOpen={openForcheckingDialog}
        title={`Update PO Status of ${poProductInData?.order_no}`}
        subTitle={`Do you want to update this PO status to "FOR CHECKING"?`}
        onHandleConfirmAction={() => onClickUpdateStatus('for_checking')}
        onHandleClose={() => setOpenForCheckingDialog(!openForcheckingDialog)}
      />
    </Page>
  );
};

export default ProductInDetails;
